import { useOfflineSync } from "@/hooks/useOfflineSync";
import { Wifi, WifiOff, RefreshCw, Cloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface OfflineSyncIndicatorProps {
  className?: string;
  showWhenOnline?: boolean;
}

export function OfflineSyncIndicator({
  className = "",
  showWhenOnline = false,
}: OfflineSyncIndicatorProps) {
  const { isOnline, pendingCount, isSyncing, sync } = useOfflineSync({
    showToasts: false, // Parent component handles toasts
  });

  // Don't show anything if online with no pending items (unless showWhenOnline is true)
  if (isOnline && pendingCount === 0 && !showWhenOnline) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Online/Offline Status */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
              isOnline
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
            }`}
          >
            {isOnline ? (
              <Wifi className="w-3.5 h-3.5" />
            ) : (
              <WifiOff className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {isOnline ? "Connected to the internet" : "No internet connection"}
        </TooltipContent>
      </Tooltip>

      {/* Pending Count */}
      {pendingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
              <Cloud className="w-3.5 h-3.5" />
              <span>{pendingCount} pending</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {pendingCount} submission{pendingCount > 1 ? "s" : ""} waiting to sync
          </TooltipContent>
        </Tooltip>
      )}

      {/* Sync Button */}
      {isOnline && pendingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={sync}
              disabled={isSyncing}
              className="h-7 px-2"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${isSyncing ? "animate-spin" : ""}`}
              />
              <span className="ml-1.5 hidden sm:inline">Sync</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Sync pending submissions now</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
